import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { useSite } from "@/context/SiteContext";
import { track } from "@/lib/api";

const VALUES = [
  { title: "Honesty before pressure", text: "You will get a straight answer — including when coverage isn't the right move." },
  { title: "Clarity before complexity", text: "Policies explained in everyday language, so you know what you're buying." },
  { title: "Service before and after the sale", text: "Questions about your coverage later? Call. That's what an agent is for." },
  { title: "Coverage chosen for your actual needs", text: "Recommendations start with your situation, your health, and your budget." },
  { title: "Respect for every budget", text: "Protection only works if you can comfortably keep it. That matters here." },
];

export default function About() {
  const site = useSite();
  return (
    <>
      <SEO
        title="About Michael Janas & 1907 Insurance Group"
        description="Meet Michael Janas, an independent licensed life insurance agent (NPN 21419333) helping families in 19 states compare final expense and life insurance options without pressure."
      />
      <PageHero
        eyebrow="About 1907 Insurance Group"
        title="Independent Guidance, One Conversation at a Time"
        intro="1907 Insurance Group exists to make life insurance understandable — and to help families choose coverage that genuinely fits their lives."
      />

      <section className="py-16 md:py-24" data-testid="about-agent">
        <div className="container-site grid items-center gap-12 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <Reveal className="relative mx-auto w-full max-w-md">
            <div className="absolute -inset-3 rounded-2xl border-2 border-gold/50" aria-hidden="true" />
            <img src="/assets/michael.png"
              alt="Portrait of Michael Janas, founder of 1907 Insurance Group"
              className="relative w-full rounded-2xl object-cover shadow-lg" loading="lazy" data-testid="about-agent-photo" />
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="text-3xl sm:text-4xl tracking-tight">Michael Janas</h2>
            <p className="mt-2 text-lg font-semibold text-gold-dark">Independent Licensed Life Insurance Agent • NPN {site.npn}</p>
            <div className="mt-6 space-y-4 text-lg text-mutedink leading-relaxed">
              <p>
                Michael Janas is an independent licensed life insurance agent and the founder of 1907 Insurance Group.
                He helps individuals and families understand their coverage options and choose policies suited to
                their needs, health, and budget.
              </p>
              <p>
                Michael believes buying life insurance should begin with a clear conversation — not pressure. His goal
                is to explain the available options in understandable terms, answer questions honestly, and help each
                client make an informed decision for their family.
              </p>
              <p>
                Because he is independent, Michael is not limited to a single insurance company. He compares options
                from the carriers he is appointed with, explains how each one evaluates health and pricing, and stays
                available after the policy is in place.
              </p>
            </div>
            <a href={`tel:${site.phone_href}`} className="btn-primary mt-8" data-testid="about-call-button"
              onClick={() => track("phone_click", { location: "about" })}>
              <Phone className="h-5 w-5" aria-hidden="true" /> Talk With Michael — {site.phone_display}
            </a>
          </Reveal>
        </div>
      </section>

      <section className="bg-mist py-16 md:py-24" data-testid="about-values">
        <div className="container-site">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl tracking-tight">How We Work</h2>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="h-full rounded-xl border border-navy-100/70 bg-white p-7 shadow-sm" data-testid={`value-card-${i}`}>
                  <h3 className="text-xl">{v.title}</h3>
                  <p className="mt-2.5 text-mutedink leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="flex h-full flex-col justify-center rounded-xl bg-navy p-7 shadow-sm" data-testid="value-cta-card">
                <p className="text-cream/90 text-lg leading-relaxed">
                  {site.states_served_text}. Call to confirm availability in your state.
                </p>
                <Link to="/quote" className="btn-accent mt-6 !text-base" data-testid="about-quote-button">Get My Quote</Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <CTASection heading="Have a Question? Just Ask." text="A short conversation is often the fastest way to understand your options. Call or request a quote — whichever is easier for you." />
    </>
  );
}
